import { FC, useEffect, useState } from 'react';
import { Link, useActionData, useLocation, useNavigate, useNavigation, useSubmit } from 'react-router-dom';

import { Alert, Button, Col, Container, FormFeedback, Input, Row } from 'reactstrap';

import preval from 'babel-plugin-preval/macro';
import classNames from 'classnames';
import { parseISO } from 'date-fns';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { BuildTime, IconButton, useLoadingStore, useReturn } from '@hsp/react-component-library';
import { ACCESS_TOKEN, useDateFormat, useDidMount, useDidUnmount, useLocalStorage, useToggle } from '@hsp/react-hooks';
import { IAuthenticationToken, isDefined, isOfType } from '@hsp/utilities';

import { useEndSession } from 'hooks/session';
import { useAppDispatch } from 'hooks/store';
import { LoginActions } from 'store/actions';
import { ILocation } from 'types/router';
import { IUserLogin } from 'types/user';
import { buildUrl } from 'utilities/strings';
import { translateToken } from 'utilities/user';

import cssClasses from './login.module.scss';

const LoginPage: FC = () => {
  const date = useDateFormat(parseISO(preval`module.exports = new Date()`), 'QQQ.yyyyMMdd.HHmmss.SSS');
  const version = +(process.env.REACT_APP_VERSION ?? 1);

  const [showPassword, togglePassword] = useToggle(false);
  const [state] = useState<IUserLogin>({
    email: '',
    password: '',
  });

  const dispatch = useAppDispatch();
  const actionData = useActionData() as Record<string, unknown>;
  const { endSession } = useEndSession();
  const formik = useFormik<IUserLogin>({
    enableReinitialize: true,
    initialValues: {
      email: state.email,
      password: state.password,
    },
    onSubmit: async (values) => {
      if (isDefined(locationState)) {
        const { from } = locationState;

        setReturnUrl(isDefined(from) ? from.pathname : undefined);
      }

      submit({ ...values }, { method: 'post' });
    },
    validationSchema: yup.object().shape({
      email: yup.string().required('Please Enter Your Email'),
      password: yup.string().required('Please Enter Valid Password'),
    }),
  });
  const setLoading = useLoadingStore(({ setIsLoading }) => setIsLoading);
  const [, setToken] = useLocalStorage<IAuthenticationToken>(ACCESS_TOKEN, null);
  const { state: locationState } = useLocation() as ILocation;
  const navigate = useNavigate();
  const navigation = useNavigation();
  const { setReturnUrl } = useReturn();
  const submit = useSubmit();

  const isError = isDefined(actionData) && 'error' in actionData;

  useDidMount(() => endSession());

  useDidUnmount(() => {
    setLoading(false);
  });

  useEffect(() => setLoading(navigation.state === 'submitting'), [navigation.state, setLoading]);

  useEffect(() => {
    if (!isDefined(actionData)) {
      return;
    }

    const isToken = isOfType<IAuthenticationToken>(actionData, 'token');

    if (isToken) {
      const url = locationState?.from ?? buildUrl('Root.children.Dashboard');

      setToken(actionData);
      dispatch(LoginActions.setUser(translateToken(actionData.token)));
      navigate(url, { replace: true });
    }
  }, [actionData, dispatch, locationState?.from, navigate, setToken]);

  return (
    <div className="vh-100 vw-100">
      <Container
        className="px-0 vh-100"
        fluid>
        <Row className={classNames(cssClasses.bgAuthentication, 'justify-content-center', 'mx-0', 'vh-100')}>
          <Col
            className={classNames(cssClasses.loginForm, 'h-100', 'px-0', 'rounded-start-lg')}
            lg={{ offset: 6, size: 6 }}
            md={12}
            xl={{ offset: 8, size: 4 }}>
            <div className="d-flex flex-column h-100 justify-content-center mx-5 position-relative">
              <div className="mb-3">
                <h3>Sign in</h3>
              </div>
              <div className="mb-3">
                {isError ? <Alert color="danger">{(actionData.error as Error).message}</Alert> : null}
                <form
                  className="form-horizontal"
                  onSubmit={formik.handleSubmit}>
                  <div className="mb-3">
                    <Input
                      autoComplete="off"
                      className={classNames('form-control', {
                        'is-invalid': formik.errors.email && formik.touched.email,
                      })}
                      name="email"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      type="email"
                      value={formik.values.email}
                    />
                    <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>
                  </div>
                  <div className="mb-3">
                    <div
                      className={classNames('input-group', {
                        'is-invalid': formik.errors.password && formik.touched.password,
                      })}>
                      <Input
                        autoComplete="off"
                        className={classNames('form-control', {
                          'is-invalid': formik.errors.password && formik.touched.password,
                        })}
                        name="password"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        type={showPassword ? 'text' : 'password'}
                        value={formik.values.password}
                      />
                      <IconButton
                        color="secondary"
                        icon={['fas', showPassword ? 'eye' : 'eye-slash']}
                        onClick={togglePassword}
                        outline={!showPassword}
                      />
                    </div>
                    <FormFeedback type="invalid">{formik.errors.password}</FormFeedback>
                  </div>
                  <div className="d-flex justify-content-end mb-3">
                    <Link
                      className="text-muted"
                      to="/forgot-password">
                      Forgot password?
                    </Link>
                  </div>

                  <div className="d-grid">
                    <Button
                      block
                      color="primary"
                      disabled={navigation.state === 'submitting'}
                      type="submit">
                      Sign in
                    </Button>
                  </div>

                  <div className="my-2 text-center text-muted">Or</div>

                  <div className="d-grid">
                    <Button
                      block
                      color="secondary"
                      type="button">
                      Sign in with LinkedIn
                    </Button>
                  </div>
                </form>
              </div>
              <div className="text-center">
                <p className="text-muted">
                  Don&apos;t have an account?
                  <Link
                    className="fw-medium ms-1 text-primary"
                    to="/register">
                    Sign up now
                  </Link>
                </p>
              </div>
              <div className={classNames('d-flex', 'text-muted', cssClasses.buildTime)}>
                <BuildTime
                  buildDate={date}
                  version={version}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default LoginPage;
